/**
 * Objective Details Page Component
 *
 * Displays detailed information about a single objective including
 * key results, check-ins, and alignment information.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import type { ObjectiveDetailsProps, DetailsTab } from './objectiveDetails.types';
import { DETAILS_TABS } from './objectiveDetails.definitions';
import { StatusBadge } from '../../components/StatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { ProgressBar } from '../../components/ProgressBar';
import { KeyResultCard } from '../../components/KeyResultCard';
import { Breadcrumb } from '../../components/Breadcrumb';
import { getLevelConfig } from '../../components/ObjectiveCard/objectiveCard.definitions';
import { useObjectiveQuery } from '../../hooks/data/useObjectiveQuery';
import { useKeyResultsQuery } from '../../hooks/data/useKeyResultsQuery';
import { useObjectiveMutations } from '../../hooks/data/useObjectiveMutations';
import { useKeyResultMutations } from '../../hooks/data/useKeyResultMutations';
import { useCheckInsRealtimeQuery } from '../../hooks/data/useCheckInsRealtimeQuery';
import { useCheckInMutations } from '../../hooks/data/useCheckInMutations';
import { useCyclesQuery } from '../../hooks/data/useCyclesQuery';
import { useAuth } from '@/contexts/AuthContext';
import CreateObjectiveModal from '../Objectives/components/CreateObjectiveModal';
import CreateKeyResultModal from '../../components/CreateKeyResultModal';
import { CheckInForm } from '../../components/CheckInForm';
import CheckInHistory from '../CheckIns/components/CheckInHistory';
import StaticCombobox from '@/components/shared/StaticCombobox/StaticCombobox';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data/hooks/useCollaboratorsPublicQuery';

export default function ObjectiveDetails({ className }: ObjectiveDetailsProps) {
  const { cycleId, objectiveId } = useParams<{ cycleId: string; objectiveId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<DetailsTab>('keyResults');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateKRModalOpen, setIsCreateKRModalOpen] = useState(false);
  const [isEditKRModalOpen, setIsEditKRModalOpen] = useState(false);
  const [editingKeyResult, setEditingKeyResult] = useState<any>(null);
  const [selectedKeyResultId, setSelectedKeyResultId] = useState<string>('');
  const [showCheckInForm, setShowCheckInForm] = useState(false);

  // Fetch objective and key results from Firestore
  const { data: objective, isLoading: isLoadingObjective, error: objectiveError } = useObjectiveQuery(objectiveId);
  const { data: keyResults = [], isLoading: isLoadingKeyResults } = useKeyResultsQuery(objectiveId);

  // Fetch collaborators for responsible photos
  const { data: collaboratorsPublic = [] } = useCollaboratorsPublicQuery();

  // Fetch cycle data for breadcrumb
  const { cycles, isLoading: cyclesLoading } = useCyclesQuery();
  const cycle = cycles.find((c) => c.id === cycleId);

  // Mutations
  const { updateObjective, archiveObjective } = useObjectiveMutations();
  const { createKeyResult, updateKeyResult, deleteKeyResult } = useKeyResultMutations();
  const { createCheckIn } = useCheckInMutations();

  // Check-ins query (only for selected KR)
  const { checkIns, isLoading: loadingCheckIns } = useCheckInsRealtimeQuery(selectedKeyResultId);

  // Auto-select first key result for check-ins
  React.useEffect(() => {
    if (activeTab === 'checkIns' && keyResults.length > 0 && !selectedKeyResultId) {
      setSelectedKeyResultId(keyResults[0].id);
    }
  }, [activeTab, keyResults, selectedKeyResultId]);

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };


  const handleUpdateObjective = async (data: any) => {
    if (!objectiveId || !user) return;

    console.log('[ObjectiveDetails] Updating objective:', objectiveId, data);

    try {
      // Only include fields that are actually in the form and can be edited
      // Fields not included: type, allowedRoles (not in ObjectiveFormSchema)
      await updateObjective.mutateAsync({
        id: objectiveId,
        data: {
          id: objectiveId,
          title: data.title,
          description: data.description,
          level: data.level,
        },
      });
      setIsEditModalOpen(false);
    } catch (err: any) {
      console.error('Error updating objective:', err);
      console.error('Error details:', err?.message, err?.errors);
      alert(`Erro ao atualizar objetivo: ${err?.message || 'Erro desconhecido'}`);
    }
  };

  const handleArchive = async () => {
    if (!objectiveId || !user) return;

    const confirmed = window.confirm('Tem certeza que deseja arquivar este objetivo?');
    if (confirmed) {
      try {
        await archiveObjective.mutateAsync({
          id: objectiveId,
          userId: user.uid,
        });
        navigate('/okr/objectives');
      } catch (err) {
        console.error('Error archiving objective:', err);
        alert('Erro ao arquivar objetivo. Tente novamente.');
      }
    }
  };

  const handleCreateKeyResult = async (data: any) => {
    if (!objectiveId || !objective || !user) return;

    try {
      await createKeyResult.mutateAsync({
        ...data,
        objectiveId,
        objectiveTitle: objective.title,
        cycleId: objective.cycleId,
      });
      setIsCreateKRModalOpen(false);
    } catch (err: any) {
      console.error('Error creating key result:', err);
      throw err; // Re-throw to be handled by modal
    }
  };

  const handleSubmitCheckIn = async (data: any) => {
    if (!user || !selectedKeyResultId) return;

    const selectedKR = keyResults.find(kr => kr.id === selectedKeyResultId);
    if (!selectedKR) return;

    try {
      // Calculate old progress percentage
      const oldProgress = selectedKR.targetValue !== selectedKR.initialValue
        ? ((selectedKR.currentValue - selectedKR.initialValue) / (selectedKR.targetValue - selectedKR.initialValue)) * 100
        : 0;

      // Calculate new progress percentage
      const newProgress = selectedKR.targetValue !== selectedKR.initialValue
        ? ((data.newValue - selectedKR.initialValue) / (selectedKR.targetValue - selectedKR.initialValue)) * 100
        : 0;

      await createCheckIn.mutateAsync({
        keyResultId: selectedKeyResultId,
        data: {
          checkInDate: new Date(), // Use Date instead of Timestamp for Zod validation
          previousValue: selectedKR.currentValue,
          newValue: data.newValue,
          progressChange: Math.round(newProgress - oldProgress),
          impediments: data.impediments,
          notes: data.notes,
          actionsRequired: data.actionsRequired,
          recordedById: user.uid,
          recordedByName: user.displayName || user.email || 'Unknown',
          recordedByEmail: user.email || '',
          cycleEventId: null,
        },
      });
      setShowCheckInForm(false);
    } catch (err) {
      console.error('Error creating check-in:', err);
      alert('Erro ao criar check-in. Tente novamente.');
    }
  };

  const handleEditKeyResult = (krId: string) => {
    const kr = keyResults.find(k => k.id === krId);
    if (kr) {
      setEditingKeyResult(kr);
      setIsEditKRModalOpen(true);
    }
  };

  const handleUpdateKeyResult = async (data: any) => {
    if (!editingKeyResult || !user) return;

    try {
      // Remove undefined values to avoid Firestore errors
      const updateData: any = {
        title: data.title,
        metricType: data.metricType,
        initialValue: data.initialValue,
        targetValue: data.targetValue,
        startDate: data.startDate,
        targetDate: data.targetDate,
        responsibleId: data.responsibleId,
        responsibleName: data.responsibleName,
        responsibleEmail: data.responsibleEmail,
        _metadata: {
          lastModifiedBy: user.uid,
        },
      };

      // Only add optional fields if they have values
      if (data.description !== undefined && data.description !== '') {
        updateData.description = data.description;
      }
      if (data.unit !== undefined && data.unit !== '') {
        updateData.unit = data.unit;
      }

      await updateKeyResult.mutateAsync({
        id: editingKeyResult.id,
        data: updateData,
      });
      setIsEditKRModalOpen(false);
      setEditingKeyResult(null);
    } catch (err: any) {
      console.error('Error updating key result:', err);
      throw err;
    }
  };

  const handleDeleteKeyResult = async (krId: string) => {
    if (!user) return;

    const kr = keyResults.find(k => k.id === krId);
    if (!kr) return;

    const confirmed = window.confirm(
      `Tem certeza que deseja excluir o Key Result "${kr.title}"?\n\nEsta ação não pode ser desfeita.`
    );

    if (confirmed) {
      try {
        await deleteKeyResult.mutateAsync(krId);
      } catch (err) {
        console.error('Error deleting key result:', err);
        alert('Erro ao excluir Key Result. Tente novamente.');
      }
    }
  };

  // Loading state
  if (isLoadingObjective) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-600"></div>
            <p className="mt-4 font-montserrat text-gray-600">Carregando objetivo...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (objectiveError || !objective) {
    return (
      <div className="p-6">
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <p className="font-montserrat text-lg text-gray-500">
            Objetivo não encontrado
          </p>
          <button
            onClick={() => navigate('/okr/objectives')}
            className="mt-4 rounded-lg bg-blue-600 px-6 py-2 font-montserrat font-medium text-white transition-colors hover:bg-blue-700"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const levelConfig = getLevelConfig(objective.level);

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'OKR', path: '/okr' },
    { label: cycle?.name || 'Ciclo', path: cycleId ? `/okr/cycles/${cycleId}` : '/okr' },
    { label: objective.title, path: undefined }, // Current page
  ];

  return (
    <>
      <div className={clsx('space-y-6 p-6', className)}>
        {/* Breadcrumb Navigation */}
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <PageHeader
          title={objective.title}
          subtitle={objective.description}
          badges={
            <>
              <span
                className={clsx(
                  'inline-flex items-center rounded-full border px-3 py-1 font-montserrat text-xs font-medium',
                  levelConfig.bgColor,
                  levelConfig.textColor,
                  levelConfig.borderColor
                )}
              >
                {levelConfig.label}
              </span>
            </>
          }
          actions={
            <>
              <button
                onClick={handleEdit}
                className="flex items-center gap-2 rounded-lg border border-blue-500 bg-blue-50 px-4 py-2 font-montserrat text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
              >
                <PencilIcon className="h-4 w-4" />
                Editar
              </button>
              <button
                onClick={handleArchive}
                className="flex items-center gap-2 rounded-lg border border-red-500 bg-red-50 px-4 py-2 font-montserrat text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
              >
                <TrashIcon className="h-4 w-4" />
                Arquivar
              </button>
            </>
          }
        />

        {/* Progress Overview */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="font-montserrat text-sm text-gray-600">Progresso Geral</p>
            <p className="mt-2 font-montserrat text-3xl font-bold text-gray-900">
              {objective.progress}%
            </p>
            <ProgressBar progress={objective.progress} className="mt-2" />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="font-montserrat text-sm text-gray-600">Key Results</p>
            <p className="mt-2 font-montserrat text-3xl font-bold text-gray-900">
              {objective.keyResultCount}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {keyResults.filter(kr => kr.status === 'on_track').length} no caminho
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {DETAILS_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'flex items-center gap-2 border-b-2 px-1 py-4 font-montserrat text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                )}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'keyResults' && (
            <div className="space-y-4">
              <div className="flex justify-between">
                <h2 className="font-montserrat text-xl font-semibold text-gray-900">
                  Key Results ({keyResults.length})
                </h2>
                <button
                  onClick={() => setIsCreateKRModalOpen(true)}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-montserrat text-sm font-medium text-white transition-colors hover:bg-green-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  Adicionar Key Result
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {keyResults.map((kr) => {
                  // Find responsible's photo from collaborators public
                  const responsible = collaboratorsPublic.find(c => c.id === kr.responsibleId);

                  // Map to KeyResultCard's KeyResult type
                  const cardKR = {
                    id: kr.id,
                    title: kr.title,
                    description: kr.description,
                    metricType: kr.metricType,
                    unit: kr.unit || '',
                    initialValue: kr.initialValue,
                    targetValue: kr.targetValue,
                    currentValue: kr.currentValue,
                    progress: kr.progress,
                    lastCheckInDate: kr.lastCheckInDate?.toDate(),
                    responsibleId: kr.responsibleId,
                    responsibleName: kr.responsibleName,
                    responsibleEmail: kr.responsibleEmail,
                    responsiblePhotoUrl: responsible?.photoUrl || undefined,
                  };

                  return (
                    <KeyResultCard
                      key={kr.id}
                      keyResult={cardKR}
                      showActions={true}
                      onEdit={handleEditKeyResult}
                      onDelete={handleDeleteKeyResult}
                    />
                  );
                })}
              </div>

              {keyResults.length === 0 && (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                  <p className="font-montserrat text-lg text-gray-500">
                    Nenhum Key Result cadastrado ainda.
                  </p>
                  <button
                    onClick={() => setIsCreateKRModalOpen(true)}
                    className="mt-4 rounded-lg bg-green-600 px-6 py-2 font-montserrat font-medium text-white transition-colors hover:bg-green-700"
                  >
                    Adicionar Primeiro Key Result
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'checkIns' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="font-montserrat text-xl font-semibold text-gray-900">
                  Check-ins
                </h2>
                <button
                  onClick={() => setShowCheckInForm(!showCheckInForm)}
                  disabled={keyResults.length === 0}
                  className={clsx(
                    'rounded-lg px-6 py-3 font-montserrat font-medium shadow-sm transition-colors',
                    keyResults.length === 0
                      ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                      : showCheckInForm
                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                  )}
                >
                  {showCheckInForm ? 'Cancelar' : '+ Registrar Check-in'}
                </button>
              </div>

              {keyResults.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
                  <p className="font-montserrat text-lg text-gray-700">
                    Nenhum Key Result cadastrado
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    Crie Key Results na aba "Key Results" para começar a fazer check-ins.
                  </p>
                </div>
              ) : (
                <>
                  {/* Key Result Selector */}
                  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <label className="mb-2 block font-montserrat text-sm font-medium text-gray-700">
                      Selecionar Key Result
                    </label>
                    <StaticCombobox
                      options={keyResults.map((kr) => ({
                        value: kr.id,
                        label: `${kr.title} - ${Math.round(kr.progress)}% concluído`,
                      }))}
                      selectedValue={selectedKeyResultId}
                      onChange={(value) => setSelectedKeyResultId(value || '')}
                      placeholder="Selecione um Key Result"
                    />
                  </div>

                  {/* Check-in Form */}
                  {showCheckInForm && selectedKeyResultId && (() => {
                    const selectedKR = keyResults.find(kr => kr.id === selectedKeyResultId);
                    return selectedKR ? (
                      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                        <h3 className="mb-4 font-montserrat text-lg font-semibold text-gray-900">
                          Novo Check-in
                        </h3>
                        <CheckInForm
                          keyResultId={selectedKR.id}
                          initialValue={selectedKR.initialValue}
                          targetValue={selectedKR.targetValue}
                          currentValue={selectedKR.currentValue}
                          onSubmit={handleSubmitCheckIn}
                          onCancel={() => setShowCheckInForm(false)}
                        />
                      </div>
                    ) : null;
                  })()}

                  {/* Check-in History */}
                  <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <h3 className="mb-4 font-montserrat text-lg font-semibold text-gray-900">
                      Histórico de Check-ins
                    </h3>
                    {loadingCheckIns ? (
                      <div className="flex justify-center py-8">
                        <div className="text-center">
                          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-600"></div>
                          <p className="mt-2 text-sm text-gray-600">Carregando check-ins...</p>
                        </div>
                      </div>
                    ) : (
                      <CheckInHistory
                        checkIns={checkIns}
                        keyResult={keyResults.find(kr => kr.id === selectedKeyResultId)}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Modals */}
      {/* Edit Modal */}
      <CreateObjectiveModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleUpdateObjective}
        editingObjective={objective}
      />

      {/* Create Key Result Modal */}
      {objective && (
        <CreateKeyResultModal
          isOpen={isCreateKRModalOpen}
          onClose={() => setIsCreateKRModalOpen(false)}
          onSubmit={handleCreateKeyResult}
          objectiveId={objectiveId!}
          objectiveTitle={objective.title}
          cycleId={objective.cycleId}
        />
      )}

      {/* Edit Key Result Modal */}
      {objective && editingKeyResult && (
        <CreateKeyResultModal
          isOpen={isEditKRModalOpen}
          onClose={() => {
            setIsEditKRModalOpen(false);
            setEditingKeyResult(null);
          }}
          onSubmit={handleUpdateKeyResult}
          objectiveId={objectiveId!}
          objectiveTitle={objective.title}
          cycleId={objective.cycleId}
          editingKeyResult={editingKeyResult}
        />
      )}
    </>
  );
}
